import { useState } from 'react';

export default function OrderForm({ defaultOpgavetype = '', title, subtitle }) {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedData, setSubmittedData] = useState(null);

  const [kundeType, setKundeType] = useState('privat');
  const [opgavetype, setOpgavetype] = useState(defaultOpgavetype || 'Specialfremstilling');
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFileError('');

    // Check file count
    if (selectedFiles.length > 5) {
      setFileError('Maksimalt 5 filer kan vedhæftes ad gangen.');
      e.target.value = '';
      setFiles([]);
      return;
    }

    // Check size limit (max 10MB per file, max 20MB total)
    let totalSize = 0;
    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        setFileError(`Filen "${file.name}" er for stor. Maks 10 MB pr. fil.`);
        e.target.value = '';
        setFiles([]);
        return;
      }
      totalSize += file.size;
    }

    if (totalSize > 20 * 1024 * 1024) {
      setFileError('Den samlede filstørrelse overskrider grænsen på 20 MB.');
      e.target.value = '';
      setFiles([]);
      return;
    }

    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Append kundeType and opgavetype
    formData.set('kunde_type', kundeType);
    formData.set('opgavetype', opgavetype);

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen.');
      }

      setSubmittedData({
        navn: formData.get('navn'),
        email: formData.get('email'),
        telefon: formData.get('telefon'),
        opgavetype: opgavetype
      });
      setStatus('success');
      form.reset();
      setFiles([]);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Der skete en fejl. Du kan også altid kontakte os direkte på tlf. 30 27 16 71.');
    }
  };

  const resetForm = () => {
    setStatus('idle');
    setSubmittedData(null);
    setErrorMessage('');
  };

  if (status === 'success') {
    return (
      <div className="bg-slate-900 border border-emerald-500/40 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl border border-emerald-500/40">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-3">
          Tak for din henvendelse{submittedData?.navn ? `, ${submittedData.navn}` : ''}!
        </h3>
        <p className="text-slate-300 max-w-lg mx-auto text-base md:text-lg mb-6 leading-relaxed">
          Vi har modtaget din forespørgsel om <strong>{submittedData?.opgavetype || 'din opgave'}</strong> og vender hurtigst muligt tilbage med et uforpligtende svar eller tilbud.
        </p>

        <div className="bg-slate-950/70 border border-slate-800 rounded-xl p-4 max-w-md mx-auto mb-8 text-sm text-slate-400 space-y-1">
          <p>Haster din opgave? Du er altid velkommen til at ringe direkte:</p>
          <a
            href="tel:30271671"
            className="inline-flex items-center gap-2 font-bold text-sky-400 hover:text-sky-300 text-base pt-1 whitespace-nowrap"
          >
            <i className="fa-solid fa-phone" aria-hidden="true" />
            <span className="whitespace-nowrap">Tlf: 30 27 16 71</span>
          </a>
        </div>

        <button
          type="button"
          onClick={resetForm}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-medium transition cursor-pointer text-sm"
        >
          <i className="fa-solid fa-arrow-rotate-left text-xs" aria-hidden="true" />
          <span>Send en ny forespørgsel</span>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 md:p-10 shadow-2xl relative overflow-hidden">
      {/* Decorative metal sheen accent */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-sky-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-950/70 border border-sky-800/50 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-3">
          <i className="fa-solid fa-paper-plane" aria-hidden="true" />
          <span>Uforpligtende tilbud & bestilling</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
          {title || 'Send en bestillingsforespørgsel'}
        </h2>
        <p className="mt-2 text-slate-400 text-sm sm:text-base leading-relaxed">
          {subtitle || 'Udfyld formularen med information om dit projekt, så vender vi hurtigt tilbage med en skarp pris eller vejledning.'}
        </p>
      </div>

      {status === 'error' && (
        <div className="mb-6 p-4 rounded-xl bg-red-950/60 border border-red-800/80 text-red-200 text-sm flex items-start gap-3">
          <i className="fa-solid fa-triangle-exclamation text-red-400 mt-0.5 text-base" aria-hidden="true" />
          <div>
            <p className="font-semibold text-red-300">Der opstod en fejl</p>
            <p className="text-red-300/90">{errorMessage}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Antispam Honeypot field (Strict Rule 12: absolutely hidden with sr-only + absolute positioning) */}
        <input
          type="text"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          className="sr-only absolute -left-[9999px]"
          aria-hidden="true"
        />

        {/* 1. Kunde type: Privat / Virksomhed */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
            Hvem henvender du dig som?
          </label>
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <button
              type="button"
              onClick={() => setKundeType('privat')}
              className={`py-2.5 px-4 rounded-lg text-sm font-medium border text-center transition cursor-pointer flex items-center justify-center gap-2 ${
                kundeType === 'privat'
                  ? 'bg-sky-600 border-sky-500 text-white font-semibold shadow-md shadow-sky-900/30'
                  : 'bg-slate-950/50 border-slate-700/80 text-slate-400 hover:text-slate-200 hover:border-slate-600'
              }`}
            >
              <i className="fa-solid fa-user text-xs" aria-hidden="true" />
              <span>Privatkunde</span>
            </button>
            <button
              type="button"
              onClick={() => setKundeType('virksomhed')}
              className={`py-2.5 px-4 rounded-lg text-sm font-medium border text-center transition cursor-pointer flex items-center justify-center gap-2 ${
                kundeType === 'virksomhed'
                  ? 'bg-sky-600 border-sky-500 text-white font-semibold shadow-md shadow-sky-900/30'
                  : 'bg-slate-950/50 border-slate-700/80 text-slate-400 hover:text-slate-200 hover:border-slate-600'
              }`}
            >
              <i className="fa-solid fa-building text-xs" aria-hidden="true" />
              <span>Virksomhed</span>
            </button>
          </div>
        </div>

        {/* 2. Kontaktoplysninger: Navn, Telefon, Email (Obligatoriske) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="navn" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Navn <span className="text-sky-400">*</span>
            </label>
            <input
              type="text"
              id="navn"
              name="navn"
              required
              placeholder="F.eks. Jens Hansen"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
            />
          </div>

          <div>
            <label htmlFor="telefon" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Telefonnummer <span className="text-sky-400">*</span>
            </label>
            <input
              type="tel"
              id="telefon"
              name="telefon"
              required
              placeholder="F.eks. 12 34 56 78"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Email <span className="text-sky-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              placeholder="jens@eksempel.dk"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
            />
          </div>
        </div>

        {/* If virksomhed, optional firmainfo */}
        {kundeType === 'virksomhed' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 animate-in fade-in duration-150">
            <div>
              <label htmlFor="virksomhedsnavn" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                Virksomhedsnavn
              </label>
              <input
                type="text"
                id="virksomhedsnavn"
                name="virksomhedsnavn"
                placeholder="F.eks. Fyns Entreprenør ApS"
                className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
              />
            </div>
            <div>
              <label htmlFor="cvr" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                CVR-nummer (valgfrit)
              </label>
              <input
                type="text"
                id="cvr"
                name="cvr"
                placeholder="12345678"
                className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
              />
            </div>
          </div>
        )}

        {/* 3. Opgavetype & Materiale */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="opgavetype" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Produkt- eller opgavetype
            </label>
            <select
              id="opgavetype"
              name="opgavetype"
              value={opgavetype}
              onChange={(e) => setOpgavetype(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm transition cursor-pointer"
            >
              <option value="Specialfremstilling">Specialfremstilling i stål</option>
              <option value="Trug og foderkrybber">Trug, foderkrybber & inventar</option>
              <option value="Svejsning og rørarbejde">Svejsning & rørarbejde</option>
              <option value="Reparation og smedeopgaver">Reparation & smedeopgaver</option>
              <option value="Have og uderum">Have- & uderumsløsninger (bedringe)</option>
              <option value="Generel forespørgsel">Generel forespørgsel / rådgivning</option>
            </select>
          </div>

          <div>
            <label htmlFor="materiale" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Foretrukket materiale
            </label>
            <select
              id="materiale"
              name="materiale"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm transition cursor-pointer"
            >
              <option value="Rustfrit stål">Rustfrit stål (AISI 304 / 316)</option>
              <option value="Sort stål / kulstofstål">Sort stål / konstruktionsstål</option>
              <option value="Aluminium">Aluminium</option>
              <option value="Galvaniseret stål">Galvaniseret stål</option>
              <option value="Ikke sikker / rådgiv mig">Ikke sikker – rådgiv mig gerne</option>
            </select>
          </div>
        </div>

        {/* 4. Mål/dimensioner, antal, ønsket levering */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor="antal" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Antal (stk.)
            </label>
            <input
              type="text"
              id="antal"
              name="antal"
              placeholder="F.eks. 1 stk. eller serie"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
            />
          </div>

          <div>
            <label htmlFor="dimensioner" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Mål / dimensioner
            </label>
            <input
              type="text"
              id="dimensioner"
              name="dimensioner"
              placeholder="F.eks. L: 200cm, B: 40cm"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
            />
          </div>

          <div>
            <label htmlFor="oensket_leveringstid" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
              Ønsket leveringstid
            </label>
            <input
              type="text"
              id="oensket_leveringstid"
              name="oensket_leveringstid"
              placeholder="Hurtigst muligt / dato"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
            />
          </div>
        </div>

        {/* 5. Beskrivelse (Obligatorisk) */}
        <div>
          <label htmlFor="beskrivelse" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
            Beskrivelse af opgaven <span className="text-sky-400">*</span>
          </label>
          <textarea
            id="beskrivelse"
            name="beskrivelse"
            required
            rows={4}
            placeholder="Beskriv hvad du ønsker lavet, funktion, særlige krav eller spørgsmål..."
            className="w-full px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm transition"
          />
        </div>

        {/* 6. Vedhæft tegning, skitse eller billede */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
            Vedhæft fil eller tegning (valgfrit)
          </label>
          <div className="relative border-2 border-dashed border-slate-700 hover:border-slate-600 rounded-xl p-4 sm:p-5 text-center bg-slate-950/60 transition cursor-pointer">
            <input
              type="file"
              name="vedhaeftning"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              id="file-upload"
            />
            <div className="flex flex-col items-center justify-center gap-1.5 pointer-events-none">
              <i className="fa-solid fa-cloud-arrow-up text-2xl text-sky-400 mb-1" aria-hidden="true" />
              <p className="text-sm font-medium text-slate-200">
                Klik eller træk filer herover
              </p>
              <p className="text-xs text-slate-500">
                Tegninger, fotos, skitser (PDF, PNG, JPG, WEBP - max 10 MB pr. fil)
              </p>
            </div>
          </div>

          {fileError && (
            <p className="mt-2 text-xs text-red-400 flex items-center gap-1.5">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              <span>{fileError}</span>
            </p>
          )}

          {files.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Valgte filer ({files.length}):
              </p>
              {files.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-xs bg-slate-950 px-3 py-1.5 rounded border border-slate-800 text-slate-300"
                >
                  <span className="truncate max-w-xs">{file.name}</span>
                  <span className="text-slate-500 font-mono">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-xl bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-500 hover:to-blue-600 text-white font-semibold text-base shadow-xl shadow-sky-950/50 transition transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-circle-notch animate-spin" aria-hidden="true" />
                <span>Sender din forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send bestillingsforespørgsel</span>
                <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
              </>
            )}
          </button>
          <p className="mt-3 text-xs text-slate-500">
            Ved indsendelse modtager vi din opgave direkte. Alle henvendelser behandles hurtigt og uforpligtende.
          </p>
        </div>
      </form>
    </div>
  );
}
